import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, Fragment as _Fragment, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "student-analysis-page" }
const _hoisted_2 = { class: "select-item" }
const _hoisted_3 = {
  key: 1,
  class: "no-data"
}
const _hoisted_4 = {
  key: 2,
  class: "tables-container"
}
const _hoisted_5 = { class: "table-section" }
const _hoisted_6 = { class: "total-exams" }
const _hoisted_7 = { class: "table-horizontal-scroll" }
const _hoisted_8 = { class: "table-vertical-scroll" }
const _hoisted_9 = {
  class: "tables-container",
  style: {"margin-top":"24px"}
}
const _hoisted_10 = { class: "table-section" }
const _hoisted_11 = {
  key: 0,
  class: "no-data",
  style: {"padding":"32px"}
}
const _hoisted_12 = {
  key: 2,
  class: "no-data",
  style: {"padding":"32px"}
}
const _hoisted_13 = {
  key: 3,
  class: "kp-result-wrap"
}
const _hoisted_14 = { class: "stat-item" }
const _hoisted_15 = { style: {"margin-top":"20px"} }
const _hoisted_16 = { class: "table-horizontal-scroll" }

import { ref, onMounted, computed, watch, h } from 'vue';
import { message, Spin, Empty } from 'ant-design-vue';
import type { ColumnType } from 'ant-design-vue/es/table';
import { useUserStore } from '@/store';
import { getStudentExamApi } from '@/servers/api/student';
import { postPassLineAnalysis, getKnowledgePointMasteryApi } from '@/servers/api/analysis';
import { postStudentAverageAnalysis } from '@/servers/api/average';

// 合并后的表格数据类型
type MergedAnalysisItem = {
  key: string;
  subject: string;
  passCount: number;
  passRate: string | number;
  averageScore: number;
  averageRank: number;
  bestRank: number;
  oneLineCount: number;
  averagePassRate: number;
  averageTopRate: number;
  top985Rate: number;
  top211Rate: number;
};

// ========== 知识点掌握度类型定义
type KpExamItem = {
  exam_id: number;
  exam_name: string | null;
  exam_year: string | null;
  exam_date: string | null;
  correct_question_count: number;
  total_question_count: number;
  mastery_rate: number | null;
};

type KnowledgePointMasteryRespData = {
  student_id: string;
  subject: string;
  knowledge_point: string;
  requested_exam_count: number;
  actual_exam_count: number;
  correct_question_count: number;
  total_question_count: number;
  mastery_rate: number | null;
  exams: KpExamItem[];
};

// 状态定义

export default /*@__PURE__*/_defineComponent({
  __name: 'StudentAnalysisPage',
  setup(__props) {

const userInfo = ref<any>(null);
const examListSelected = ref<any[]>([]);
const examList = ref<any[]>([]);
const isLoading = ref(true);
const tableKey = ref(Date.now());
const examAnalysisRaw = ref<any[]>([]);
const comprehensiveAnalysisRaw = ref<any[]>([]);

// 科目映射（中文科目名）
const subjects_map = ref<{[key: string]: string}>({
  '地理':'地理','化学':'化学','历史':'历史','生物':'生物','数学':'数学',
  '物理':'物理','英语':'英语','语文':'语文','政治':'政治','总分':'总分'
});

// 学期选择
const default_term = [{label:'所有成绩',value:'all'}];
const termList = ref<{label:string,value:string}[]>(default_term);
const termSelected = ref<string>(default_term[0].value);

// ========== 知识点模块状态 ==========
const kpSubject = ref('');
const kpKnowledgePoint = ref('');
const kpN = ref<number>(10);
const kpLoading = ref(false);
const kpResult = ref<KnowledgePointMasteryRespData|null>(null);

// 知识点科目下拉选项（从subjects_map生成）
const kpSubjectOptions = computed(()=>{
  return Object.values(subjects_map.value).map(label=>({label,value:label}));
});

/**
 * 获取掌握度文字颜色
 * @param rate 0‑1小数，null代表无数据
 */
const getMasteryRateColor = (rate: number | null) => {
  if (rate === null || Number.isNaN(rate)) return '';
  const pct = rate * 100;
  if (pct < 60) return '#f5222d';
  else if (pct <= 80) return '#fa8c16';
  else return '#52c41a'; //绿色
};

// 知识点表格列
const kpTableColumns: ColumnType<KpExamItem>[] = [
  { title:'考试ID', dataIndex:'exam_id', align:'center', width:90 },
  { title:'考试名称', dataIndex:'exam_name', align:'center', width:220, customRender:({value})=> value ?? '-' },
  { title:'考试年份', dataIndex:'exam_year', align:'center', width:100, customRender:({value})=> value ?? '-' },
  { title:'考试日期', dataIndex:'exam_date', align:'center', width:120, customRender:({value})=> value ?? '-' },
  { title:'正确题数', dataIndex:'correct_question_count', align:'center', width:110 },
  { title:'总题数', dataIndex:'total_question_count', align:'center', width:110 },
  {
    title:'本场掌握度',
    dataIndex:'mastery_rate',
    align:'center',
    width:130,
    customRender:({value})=>{
      if(value === null) return '-';
      const color = getMasteryRateColor(value);
      return h('span', { style:{color, fontWeight:500} }, `${(value * 100).toFixed(2)}%`);
    }
  }
];

// 合并后的表格数据
const mergedAnalysisList = computed<MergedAnalysisItem[]>(() => {
  if (!examAnalysisRaw.value.length || !comprehensiveAnalysisRaw.value.length) return [];

  return examAnalysisRaw.value.map(examItem => {
    const compItem = comprehensiveAnalysisRaw.value.find((item: any) => item.subject === examItem.subject);
    return {
      ...examItem,
      passRate: examItem.passRate || '0',
      averageScore: compItem?.averageScore || 0,
      averageRank: compItem?.averageRank || 0,
      bestRank: compItem?.bestRank || 0,
      oneLineCount: compItem?.oneLineCount || 0,
      averagePassRate: compItem?.averagePassRate || 0,
      averageTopRate: compItem?.averageTopRate || 0,
      top985Rate: compItem?.top985Rate || 0,
      top211Rate: compItem?.top211Rate || 0,
    };
  });
});

const getPassRateColor = (text: string) => {
  // 处理百分比格式
  let rateValue: number;
  
  if (typeof text === 'string' && text.includes('%')) {
    // 去掉百分号并转换为数字
    rateValue = parseFloat(text.replace('%', ''));
  } else {
    // 如果是小数格式，转换为百分比
    rateValue = parseFloat(text) * 100;
  }
  
  // 处理转换失败的情况
  if (isNaN(rateValue)) {
    console.warn('无效的特控进线率数据:', text);
    return 'rgba(0, 0, 0, 0.85)';
  }
  
  if (rateValue < 60) {
    return '#f5222d'; // 红色：低于60%
  } else if (rateValue < 80) {
    return 'orange'; // 橙色：60%到80%之间
  } else {
    return '#52c41a'; // 绿色：高于80%
  }
};

// 表格列定义
const mergedStatsColumns: ColumnType[] = [
  {
    title: '科目',
    dataIndex: 'subject',
    key: 'subject',
    align: 'center',
    width: 100,
    fixed: 'left',
    customCell: () => ({ style: { backgroundColor: '#f7f9fc', fontWeight: '600' } }),
  },
  {
    title: '特控进线次数',
    dataIndex: 'passCount',
    key: 'passCount',
    align: 'center',
    width: 130,
    customRender: ({ value }: { value: number }) => value || 0,
  },
  {
    title: '特控进线率',
    dataIndex: 'passRate',
    key: 'passRate',
    align: 'center',
    width: 120,
    customRender: ({ text }: { text: string }) => text,
    customCell: (record: any) => {
      const text = record.passRate || '0';
      const color = getPassRateColor(text);
      return { style: { color, fontWeight: '500' } };
    },
  },
  {
    title: '平均分',
    dataIndex: 'averageScore',
    key: 'averageScore',
    align: 'center',
    width: 100,
    customRender: ({ value }: { value: number }) => value.toFixed(2),
  },
  {
    title: '平均排名',
    dataIndex: 'averageRank',
    key: 'averageRank',
    align: 'center',
    width: 110,
    customRender: ({ value }: { value: number }) => Math.round(value),
  },
  {
    title: '最佳排名',
    dataIndex: 'bestRank',
    key: 'bestRank',
    align: 'center',
    width: 110,
    customRender: ({ value }: { value: number }) => value || 0,
  },
  {
    title: '特控达线次数',
    dataIndex: 'oneLineCount',
    key: 'oneLineCount',
    align: 'center',
    width: 130,
    customRender: ({ value }: { value: number }) => value || 0,
  },
  {
    title: '平均及格率',
    dataIndex: 'averagePassRate',
    key: 'averagePassRate',
    align: 'center',
    width: 120,
    customRender: ({ value }: { value: number }) => (value * 100).toFixed(2) + '%',
  },
  {
    title: '清北率',
    dataIndex: 'averageTopRate',
    key: 'averageTopRate',
    align: 'center',
    width: 120,
    customRender: ({ value }: { value: number }) => (value * 100).toFixed(2) + '%',
  },
  {
    title: '985达线率',
    dataIndex: 'top985Rate',
    key: 'top985Rate',
    align: 'center',
    width: 120,
    customRender: ({ value }: { value: number }) => (value * 100).toFixed(2) + '%',
  },
  {
    title: '211达线率',
    dataIndex: 'top211Rate',
    key: 'top211Rate',
    align: 'center',
    width: 120,
    customRender: ({ value }: { value: number }) => (value * 100).toFixed(2) + '%',
  },
];

// 根据学生选科过滤科目映射
const bindStudentSubject = () => {
  const selected_subjects = userInfo.value?.student?.subject_selection;
  if (!selected_subjects) {
    message.warn('学生还未选科，默认显示所有科目');
    return;
  }
  if (selected_subjects.indexOf('物') === -1) delete subjects_map.value['物理'];
  if (selected_subjects.indexOf('化') === -1) delete subjects_map.value['化学'];
  if (selected_subjects.indexOf('政') === -1) delete subjects_map.value['政治'];
  if (selected_subjects.indexOf('生') === -1) delete subjects_map.value['生物'];
  if (selected_subjects.indexOf('史') === -1) delete subjects_map.value['历史'];
  if (selected_subjects.indexOf('地') === -1) delete subjects_map.value['地理'];
};

// 获取学生信息
const fetchStudentInfo = async () => {
  try {
    const userStore = useUserStore();
    userInfo.value = userStore.userInfo;
    if (!userInfo.value?.role) {
      message.error('未找到学生信息，请重新登录');
      isLoading.value = false;
      return;
    }
    bindStudentSubject();
  } catch (err) {
    message.error('获取学生信息失败');
    isLoading.value = false;
  }
};

// 获取考试列表
const fetchExamList = async () => {
  try {
    const studentId = userInfo.value?.role;
    if (!studentId) return;
    const res = await getStudentExamApi({ student_uid: studentId });
    if (res.code === 200 && res.data.length) {
      examList.value = res.data.map((item: any) => ({
        value: item[0].id, label: item[0].name, year: item[0].year
      }));
      const years = Array.from(new Set(res.data.map((item: any) => String(item[0].year)))) as string[];
      termList.value = [...default_term, ...years.map((year): { label: string; value: string } => ({ label: year, value: year }))];
      termSelected.value = termList.value[0].value;
      examListSelected.value = examList.value.map(item => item.value);
    } else {
      message.warning('未查询到考试数据');
    }
  } catch (err) {
    message.error('获取考试列表失败');
  }
};

// 获取考试统计数据
const fetchExamStats = async () => {
  try {
    const studentId = userInfo.value?.role;
    if (!studentId || !examListSelected.value.length) return;
    
    interface PassLineItem {
      pass_count: number;
      pass_rate: string | number;
    }
    interface PassLineResponse {
      [key: string]: PassLineItem;
    }
    const res = await postPassLineAnalysis({ 
      student_id: studentId, 
      exam_ids: examListSelected.value 
    } as any) as { code: number; data?: PassLineResponse };
    
    if (res.code === 200 && res.data) {
      examAnalysisRaw.value = Object.entries(res.data)
        .filter(([key]) => key in subjects_map.value)
        .map(([key, data]: [string, PassLineItem]) => ({
          key,
          subject: subjects_map.value[key] || key,
          passCount: data.pass_count || 0,
          passRate: data.pass_rate || '0',
        }));
    } else {
      examAnalysisRaw.value = [];
    }
  } catch (err) {
    message.error('获取考试统计数据失败,缺少考试分数线');
    examAnalysisRaw.value = [];
  }
};

// 获取综合分析数据
const fetchComprehensiveAnalysis = async () => {
  try {
    const studentId = userInfo.value?.student?.uid;
    if (!studentId || !examListSelected.value.length) return;

    interface StudentAverageAnalysisResponse {
      code: number;
      msg: string;
      data: Array<{
        id: number;
        class_id: number;
        student_id: string;
        average_rank: { [key: string]: number };
        average_pass_rate: { [key: string]: number };
        average_top_rate: { [key: string]: number };
        one_line_count: { [key: string]: number };
        top_985_rate: { [key: string]: number };
        top_211_rate: { [key: string]: number };
        best_rank: { [key: string]: number };
        average_score: { [key: string]: number };
      }>;
    }

    const res = await postStudentAverageAnalysis({
      student_id: studentId,
      exam_ids: examListSelected.value
    } as any) as StudentAverageAnalysisResponse;

    if (res.code === 200 && res.data && res.data.length > 0) {
      const compData = res.data[0];

      comprehensiveAnalysisRaw.value = Object.entries(subjects_map.value)
        .filter(([key]) => compData.average_score[key] !== undefined)
        .map(([key, subject]) => ({
          key,
          subject,
          averageScore: compData.average_score[key] || 0,
          averageRank: compData.average_rank[key] || 0,
          bestRank: compData.best_rank[key] || 0,
          oneLineCount: compData.one_line_count[key] || 0,
          averagePassRate: compData.average_pass_rate[key] || 0,
          averageTopRate: compData.average_top_rate[key] || 0,
          top985Rate: compData.top_985_rate[key] || 0,
          top211Rate: compData.top_211_rate[key] || 0,
        }));
    } else {
      comprehensiveAnalysisRaw.value = [];
    }
  } catch (err) {
    message.error('获取综合分析数据失败');
    comprehensiveAnalysisRaw.value = [];
  }
};

function safeMasteryRate(raw: number | null | undefined): number | null {
  if(raw === null || raw === undefined || Number.isNaN(raw)){
    return null
  }
  return raw
}

// ========== 查询知识点掌握度 ==========
const queryKnowledgePoint = async () => {
  if(!kpSubject.value || !kpKnowledgePoint.value) {
    message.warn('请选择科目并填写知识点');
    return;
  }
  const studentId = userInfo.value?.role;
  if(!studentId) {
    message.error('学生ID不存在');
    return;
  }
  kpLoading.value = true;
  kpResult.value = null;
  try {
    const res = await getKnowledgePointMasteryApi({
      student_id: studentId,
      subject: kpSubject.value,
      knowledge_point: kpKnowledgePoint.value,
      n: kpN.value
    });
    if(res.code === 200) {
      kpResult.value = res.data as KnowledgePointMasteryRespData;
    } else {
      message.error(res.msg || '查询知识点掌握度失败');
    }
  }catch(err) {
    message.error('接口请求异常');
  }finally {
    kpLoading.value = false;
  }
};

// 提交按钮
const submit = async () => {
  isLoading.value = true;
  examListSelected.value = termSelected.value === 'all'
    ? examList.value.map(item => item.value)
    : examList.value.filter(item => item.year === termSelected.value).map(item => item.value);
  
  await Promise.all([fetchExamStats(), fetchComprehensiveAnalysis()]);
  tableKey.value = Date.now();
  isLoading.value = false;
};

// 页面加载
onMounted(async () => {
  await fetchStudentInfo();
  await fetchExamList();
  await submit();
});

// 监听学期变化
watch(termSelected, () => {
  if (termList.value.length > 1) submit();
});

return (_ctx: any,_cache: any) => {
  const _component_a_select = _resolveComponent("a-select")!
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_space = _resolveComponent("a-space")!
  const _component_a_spin = _resolveComponent("a-spin")!
  const _component_a_empty = _resolveComponent("a-empty")!
  const _component_a_table = _resolveComponent("a-table")!
  const _component_a_input = _resolveComponent("a-input")!
  const _component_a_input_number = _resolveComponent("a-input-number")!
  const _component_a_statistic = _resolveComponent("a-statistic")!
  const _component_a_col = _resolveComponent("a-col")!
  const _component_a_row = _resolveComponent("a-row")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_a_space, { class: "selector" }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _cache[4] || (_cache[4] = _createElementVNode("span", null, "学期选择：", -1)),
          _createVNode(_component_a_select, {
            options: termList.value,
            value: termSelected.value,
            "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((termSelected).value = $event))
          }, null, 8, ["options", "value"])
        ]),
        _createVNode(_component_a_button, {
          type: "primary",
          onClick: submit
        }, {
          default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
            _createTextVNode("确定", -1)
          ]))]),
          _: 1
        })
      ]),
      _: 1
    }),
    (isLoading.value)
      ? (_openBlock(), _createBlock(_component_a_spin, {
          key: 0,
          tip: "数据加载中...",
          style: {"margin-top":"40px","display":"block","text-align":"center"}
        }))
      : (!mergedAnalysisList.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_component_a_empty, { description: "未查询到数据，请检查学期选择或网络状态" })
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("h2", null, [
                _cache[6] || (_cache[6] = _createTextVNode("考试统计 ", -1)),
                _createElementVNode("span", _hoisted_6, "共统计" + _toDisplayString(examListSelected.value.length) + "场考试", 1)
              ]),
              _cache[7] || (_cache[7] = _createElementVNode("p", { class: "description" }, [
                _createTextVNode(" 说明：特控进线率低于60%为"),
                _createElementVNode("span", { style: {"color":"red"} }, "红色"),
                _createTextVNode("，在60%到80%之间为"),
                _createElementVNode("span", { style: {"color":"orange"} }, "橙色"),
                _createTextVNode("，高于80%为"),
                _createElementVNode("span", { style: {"color":"green"} }, "绿色"),
                _createTextVNode("。 ")
              ], -1)),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  (_openBlock(), _createBlock(_component_a_table, {
                    columns: mergedStatsColumns,
                    "data-source": mergedAnalysisList.value,
                    pagination: false,
                    size: "middle",
                    bordered: "",
                    key: tableKey.value,
                    scroll: { x: 'max-content' }
                  }, null, 8, ["data-source"]))
                ])
              ])
            ])
          ])),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("div", _hoisted_10, [
        _cache[11] || (_cache[11] = _createElementVNode("h2", null, "知识点掌握情况", -1)),
        _createVNode(_component_a_space, {
          class: "kp-selector",
          style: {"margin":"16px 0"}
        }, {
          default: _withCtx(() => [
            _createVNode(_component_a_select, {
              value: kpSubject.value,
              "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((kpSubject).value = $event)),
              placeholder: "选择科目",
              options: kpSubjectOptions.value,
              style: {"width":"140px"}
            }, null, 8, ["value", "options"]),
            _createVNode(_component_a_input, {
              value: kpKnowledgePoint.value,
              "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((kpKnowledgePoint).value = $event)),
              placeholder: "输入知识点名称",
              style: {"width":"260px"}
            }, null, 8, ["value"]),
            _createVNode(_component_a_input_number, {
              value: kpN.value,
              "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((kpN).value = $event)),
              min: 1,
              max: 100,
              placeholder: "最近N场",
              style: {"width":"120px"}
            }, null, 8, ["value"]),
            _createVNode(_component_a_button, {
              type: "primary",
              onClick: queryKnowledgePoint,
              loading: kpLoading.value
            }, {
              default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                _createTextVNode("查询", -1)
              ]))]),
              _: 1
            }, 8, ["loading"])
          ]),
          _: 1
        }),
        (!kpSubject.value || !kpKnowledgePoint.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
              _createVNode(_component_a_empty, { description: "请选择科目、填写知识点后点击查询" })
            ]))
          : (kpLoading.value)
            ? (_openBlock(), _createBlock(_component_a_spin, {
                key: 1,
                tip: "知识点数据加载中...",
                style: {"display":"block","margin":"30px 0","text-align":"center"}
              }))
            : (kpResult.value && kpResult.value.actual_exam_count === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                  _createVNode(_component_a_empty, { description: "未匹配到该知识点的考试答题数据" })
                ]))
              : (kpResult.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                    _createVNode(_component_a_row, {
                      gutter: [16,16],
                      class: "kp-summary-row"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_a_col, { span: 6 }, {
                          default: _withCtx(() => [
                            _createVNode(_component_a_statistic, {
                              title: "请求统计考试数",
                              value: kpResult.value.requested_exam_count
                            }, null, 8, ["value"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_a_col, { span: 6 }, {
                          default: _withCtx(() => [
                            _createVNode(_component_a_statistic, {
                              title: "实际有效考试场数",
                              value: kpResult.value.actual_exam_count
                            }, null, 8, ["value"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_a_col, { span: 6 }, {
                          default: _withCtx(() => [
                            _createVNode(_component_a_statistic, {
                              title: "总答题数",
                              value: kpResult.value.total_question_count
                            }, null, 8, ["value"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_a_col, { span: 6 }, {
                          default: _withCtx(() => [
                            _createVNode(_component_a_statistic, {
                              title: "正确题数",
                              value: kpResult.value.correct_question_count
                            }, null, 8, ["value"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_a_col, { span: 6 }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_14, [
                              _cache[9] || (_cache[9] = _createElementVNode("div", { class: "stat-title" }, "整体掌握度", -1)),
                              _createElementVNode("div", {
                                class: "stat-value",
                                style: _normalizeStyle({ color:getMasteryRateColor(safeMasteryRate(kpResult.value?.mastery_rate)),fontWeight:500 })
                              }, [
                                (safeMasteryRate(kpResult.value?.mastery_rate) !== null)
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                      _createTextVNode(_toDisplayString((safeMasteryRate(kpResult.value?.mastery_rate)! * 100).toFixed(2)) + "% ", 1)
                                    ], 64))
                                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                      _createTextVNode("--")
                                    ], 64))
                              ], 4)
                            ])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createElementVNode("div", _hoisted_15, [
                      _cache[10] || (_cache[10] = _createElementVNode("h4", null, "逐场考试明细", -1)),
                      _createElementVNode("div", _hoisted_16, [
                        _createVNode(_component_a_table, {
                          columns: kpTableColumns,
                          "data-source": kpResult.value.exams,
                          pagination: false,
                          bordered: "",
                          size: "middle",
                          "row-key": "exam_id",
                          scroll: { x:'max-content' }
                        }, null, 8, ["data-source"])
                      ])
                    ])
                  ]))
                : _createCommentVNode("", true)
      ])
    ]),
    _cache[12] || (_cache[12] = _createElementVNode("div", { class: "bottom-space" }, null, -1))
  ]))
}
}

})